/**
 * Toast Context
 *
 * Provides a global toast notification system for the application.
 * Toasts appear in the bottom-right corner and auto-dismiss after 3 seconds.
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { usePlatformRuntime } from '../runtime';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (message: string, type: ToastType) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const runtime = usePlatformRuntime();
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const enqueueToast = useCallback(
    (message: string, type: ToastType) => {
      const id = runtime.toasts.createToastId();
      const newToast = { id, message, type };

      setToasts((prev) => [...prev, newToast]);

      setTimeout(() => {
        removeToast(id);
      }, runtime.toasts.autoDismissMs);
    },
    [runtime.toasts, removeToast]
  );

  const addToast = useCallback(
    (message: string, type: ToastType) => {
      enqueueToast(message, type);
    },
    [enqueueToast]
  );

  useEffect(() => {
    return runtime.toasts.subscribeToToasts((toast) => {
      enqueueToast(toast.message, toast.type);
    });
  }, [runtime.toasts, enqueueToast]);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
    </ToastContext.Provider>
  );
};
